import { useCallback, useEffect, useMemo, useState } from 'react';

export const buildJsonPath = (
  parentPath: string,
  segment: string | number,
) => {
  if (typeof segment === 'number') {
    return `${parentPath}[${segment}]`;
  }
  return `${parentPath}.${segment}`;
};

const isExpandable = (value: unknown) => {
  if (value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }
  return false;
};

const enumerateExpandablePaths = (
  value: unknown,
  currentPath: string,
  currentDepth: number,
  defaultExpandedDepth: number,
  paths: Set<string>,
) => {
  if (!isExpandable(value)) return;

  if (currentDepth <= defaultExpandedDepth) {
    paths.add(currentPath);
  }

  if (currentDepth >= defaultExpandedDepth) return;

  if (Array.isArray(value)) {
    value.forEach((child, index) => {
      enumerateExpandablePaths(
        child,
        buildJsonPath(currentPath, index),
        currentDepth + 1,
        defaultExpandedDepth,
        paths,
      );
    });
    return;
  }

  if (typeof value === 'object' && value) {
    Object.keys(value as Record<string, unknown>).forEach(key => {
      enumerateExpandablePaths(
        (value as Record<string, unknown>)[key],
        buildJsonPath(currentPath, key),
        currentDepth + 1,
        defaultExpandedDepth,
        paths,
      );
    });
  }
};

type UseJsonObjectViewerExpansionArgs = {
  data: unknown;
  defaultExpandedDepth?: number;
};

export const useJsonObjectViewerExpansion = ({
  data,
  defaultExpandedDepth = 2,
}: UseJsonObjectViewerExpansionArgs) => {
  const initialExpandedPaths = useMemo(() => {
    const paths = new Set<string>();
    // Always expand root if it's expandable
    enumerateExpandablePaths(data, 'root', 0, defaultExpandedDepth, paths);
    return paths;
  }, [data, defaultExpandedDepth]);

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    () => initialExpandedPaths,
  );

  useEffect(() => {
    setExpandedPaths(initialExpandedPaths);
  }, [initialExpandedPaths]);

  const toggleExpanded = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (path: string) => expandedPaths.has(path),
    [expandedPaths],
  );

  return {
    isExpanded,
    toggleExpanded,
    expandedPaths,
  };
};

